import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client';

import { RECORD_FEATURE_WIZARD_EVENT } from '../../graphql/mutations';

export type WizardEventType = 'ARRIVED' | 'OPENED' | 'STEP_VIEWED' | 'STEP_SUBMITTED' | 'COMPLETED' | 'DISMISSED';

export type WizardEvent = {
  eventType: WizardEventType;
  /** Required for the two step events, rejected by the API when it is not a step of the wizard. */
  stepPosition?: number;
  payload?: Record<string, unknown>;
};

export type SendResult = {
  ok: boolean;
  /** The server rejected the input itself, so no retry can fix it. */
  permanent: boolean;
  /** The wizard changed under an open overlay. Retrying cannot help; the overlay must close. */
  stale: boolean;
};

export type WizardRecorder = {
  /** One attempt. For the two calls a reader retries by pressing the button again. */
  record: (event: WizardEvent) => Promise<SendResult>;
  /** Retried on the host's own initiative, for the events no reader is waiting on. */
  recordWithRetry: (event: WizardEvent) => Promise<SendResult>;
};

/** Backs off rather than hammering: a recording nobody is waiting on can afford to wait. */
const RETRY_DELAYS_MS = [800, 1600, 3200, 6400];

/** Codes the server uses for "this input is wrong", which no retry can fix. */
const PERMANENT_ERROR_CODES = ['BAD_USER_INPUT', 'VALIDATION_ERROR'];

/** The two events that name a step, and so the only two a stale wizard can reject. */
const STEP_EVENTS: WizardEventType[] = ['STEP_VIEWED', 'STEP_SUBMITTED'];

const FAILED: SendResult = { ok: false, permanent: false, stale: false };

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isPermanentRejection(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: { extensions?: { code?: unknown } }[] } | null)?.graphQLErrors;
  if (!graphQLErrors?.length) return false;
  return graphQLErrors.some((graphQLError) => PERMANENT_ERROR_CODES.includes(String(graphQLError.extensions?.code)));
}

/**
 * A rejected step event means the wizard's steps were edited while this overlay was open, so it
 * is a stale wizard rather than a transient failure and every retry would fail the same way.
 */
function isStaleStep(event: WizardEvent, permanent: boolean): boolean {
  if (!permanent) return false;
  return STEP_EVENTS.includes(event.eventType);
}

/**
 * The eventing side of the overlay, and the reason it is a hook rather than inline effects:
 * every event but ARRIVED is serialized behind a successful ARRIVED, because ARRIVED is the
 * only event that creates the state row and anything else would have nothing to write against.
 * A dismissal raised while the arrival is still in flight is therefore held and sent once the
 * row exists rather than dropped.
 *
 * Nothing here decides what the overlay shows. `myFeatureWizard` alone decides that, so a
 * failed recording never hides or blocks the wizard.
 */
export function useWizardRecorder(slug: string): WizardRecorder {
  const [recordEvent] = useMutation(RECORD_FEATURE_WIZARD_EVENT);
  const liveRef = useRef(true);
  const arrivedRef = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const attempt = useCallback(
    async (event: WizardEvent): Promise<SendResult> => {
      try {
        await recordEvent({
          variables: {
            input: {
              slug,
              eventType: event.eventType,
              stepPosition: event.stepPosition ?? null,
              payload: event.payload ?? null,
            },
          },
        });
        return { ok: true, permanent: false, stale: false };
      } catch (error) {
        const permanent = isPermanentRejection(error);
        return { ok: false, permanent, stale: isStaleStep(event, permanent) };
      }
    },
    [recordEvent, slug]
  );

  const attemptWithBackoff = useCallback(
    async (event: WizardEvent, delays: number[]): Promise<SendResult> => {
      const result = await attempt(event);
      if (result.ok || result.permanent || delays.length === 0 || !liveRef.current) return result;
      await wait(delays[0]);
      if (!liveRef.current) return result;
      return attemptWithBackoff(event, delays.slice(1));
    },
    [attempt]
  );

  /**
   * The single ARRIVED for this landing, deduplicated so the overlay's own arrival and a
   * queued dismissal cannot each start one. A run that ends up failing clears the cache, so
   * the next event re-attempts the row rather than inheriting a permanent refusal.
   */
  const ensureArrived = useCallback(async (): Promise<boolean> => {
    if (!arrivedRef.current) {
      arrivedRef.current = attemptWithBackoff({ eventType: 'ARRIVED' }, RETRY_DELAYS_MS).then((result) => result.ok);
    }
    const ok = await arrivedRef.current;
    if (!ok) arrivedRef.current = null;
    return ok;
  }, [attemptWithBackoff]);

  const send = useCallback(
    async (event: WizardEvent, delays: number[]): Promise<SendResult> => {
      if (event.eventType === 'ARRIVED') return { ok: await ensureArrived(), permanent: false, stale: false };
      const arrived = await ensureArrived();
      if (!arrived) return FAILED;
      return attemptWithBackoff(event, delays);
    },
    [attemptWithBackoff, ensureArrived]
  );

  return useMemo(
    () => ({
      record: (event: WizardEvent) => send(event, []),
      recordWithRetry: (event: WizardEvent) => send(event, RETRY_DELAYS_MS),
    }),
    [send]
  );
}
